import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  format,
  addDays,
  addMonths,
  parseISO,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  setHours,
  setMinutes
} from "date-fns";
import { APPROVAL_BELL_SLOTS } from "../components/ApprovalBell";
import "./TodoPage.css";

const REPEAT_OPTIONS = [
  { value: "none", label: "Never" },
  { value: "daily", label: "Daily" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom" }
];

const SMART_VIEWS = [
  { id: "myday", label: "My Day", icon: "\u2600" },
  { id: "important", label: "Important", icon: "\u2605" },
  { id: "assigned", label: "Assigned to me", icon: "\u25CE" },
  { id: "all", label: "All Family Tasks", icon: "\u2630" }
];

function todayStr() {
  return format(new Date(), "yyyy-MM-dd");
}
function tomorrowStr() {
  return format(addDays(new Date(), 1), "yyyy-MM-dd");
}

// Store dates as local strings (no timezone) so date comparisons stay stable.
function toLocalDateTime(date) {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}
function toLocalDate(date) {
  return format(date, "yyyy-MM-dd");
}
function hasTimePart(value) {
  return typeof value === "string" && value.length > 10 && value.includes("T");
}
function parseLocal(value) {
  if (!value) return null;
  const s = String(value).length <= 10 ? `${value}T00:00:00` : value;
  const d = parseISO(s);
  return isValid(d) ? d : null;
}
function dayLabel(date) {
  const ds = format(date, "yyyy-MM-dd");
  if (ds === todayStr()) return "Today";
  if (ds === tomorrowStr()) return "Tomorrow";
  return format(date, "EEE, MMM d");
}
function timeLabel(date) {
  return format(date, "h:mm a");
}

function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/);
  if (!parts[0]) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function formatDueLabel(due) {
  const d = parseLocal(due);
  if (!d) return due || null;
  const base = dayLabel(d);
  const showTime = hasTimePart(due) && !(d.getHours() === 0 && d.getMinutes() === 0);
  return showTime ? `${base}, ${timeLabel(d)}` : base;
}

function isOverdue(due, completed) {
  if (!due || completed) return false;
  return String(due).slice(0, 10) < todayStr();
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function TodoPage({ apiBase, familyMembers = [], userEmail, memberId, onChangeMemberId }) {
  const [tasks, setTasks] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("myday");
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [quickAdd, setQuickAdd] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newListOpen, setNewListOpen] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [manageMembersOpen, setManageMembersOpen] = useState(false);
  const [deleteListOpen, setDeleteListOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [swipeOpenTaskId, setSwipeOpenTaskId] = useState(null);
  const swipeStartXRef = useRef(0);
  const swipeTaskIdRef = useRef(null);
  const swipeDraggingRef = useRef(false);

  // Per-person key used for My Day stickiness and reminders.
  const myKey = memberId || userEmail || null;

  const memberById = useMemo(() => {
    const map = {};
    for (const m of familyMembers) map[m.id] = m;
    return map;
  }, [familyMembers]);

  const loadData = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetTodos`, { credentials: "include" });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      setLists(Array.isArray(data.lists) ? data.lists : []);
    } catch (err) {
      setError(err?.message || "Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Prompt for identity once if we can't resolve who "me" is.
  useEffect(() => {
    if (!loading && !memberId && familyMembers.length > 0) {
      setIdentityOpen(true);
    }
  }, [loading, memberId, familyMembers.length]);

  /* ----------------------------- API actions ----------------------------- */

  const createTask = useCallback(async (payload) => {
    const res = await fetch(`${apiBase}/api/CreateTodo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`Error ${res.status}`);
    const data = await res.json();
    setTasks((prev) => [data.todo, ...prev]);
    return data.todo;
  }, [apiBase]);

  const patchTask = useCallback(async (id, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      const res = await fetch(`${apiBase}/api/UpdateTodo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, ...patch })
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setTasks((prev) => prev.map((t) => (t.id === id ? data.todo : t)));
      return data.todo;
    } catch (err) {
      loadData();
      throw err;
    }
  }, [apiBase, loadData]);

  const removeTask = useCallback(async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    if (selectedTaskId === id) setSelectedTaskId(null);
    if (swipeOpenTaskId === id) setSwipeOpenTaskId(null);
    try {
      await fetch(`${apiBase}/api/DeleteTodo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id })
      });
    } catch {
      loadData();
    }
  }, [apiBase, selectedTaskId, swipeOpenTaskId, loadData]);

  const onRowTouchStart = useCallback((taskId, e) => {
    if (!e.touches?.[0]) return;
    swipeDraggingRef.current = true;
    swipeTaskIdRef.current = taskId;
    swipeStartXRef.current = e.touches[0].clientX;
  }, []);

  const onRowTouchMove = useCallback((taskId, e) => {
    if (!swipeDraggingRef.current) return;
    if (swipeTaskIdRef.current !== taskId) return;
    const x = e.touches?.[0]?.clientX;
    if (typeof x !== "number") return;
    const dx = x - swipeStartXRef.current;
    if (dx < -30) setSwipeOpenTaskId(taskId);
    else if (dx > 30) setSwipeOpenTaskId(null);
  }, []);

  const onRowTouchEnd = useCallback(() => {
    swipeDraggingRef.current = false;
    swipeTaskIdRef.current = null;
  }, []);

  const createList = useCallback(async ({ name, members }) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    setCreatingList(true);
    try {
      const res = await fetch(`${apiBase}/api/CreateTodoList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: trimmed,
          members: members || [],
          createdBy: memberId || userEmail || null
        })
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setLists((prev) => [...prev, data.list]);
      setNewListOpen(false);
      setActiveView(`list:${data.list.id}`);
    } catch (err) {
      setError(err?.message || "Could not create list");
    } finally {
      setCreatingList(false);
    }
  }, [apiBase, memberId, userEmail]);

  const patchList = useCallback(async (id, patch) => {
    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      await fetch(`${apiBase}/api/UpdateTodoList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, ...patch })
      });
    } catch {
      loadData();
    }
  }, [apiBase, loadData]);

  const removeList = useCallback(async (id) => {
    setLists((prev) => prev.filter((l) => l.id !== id));
    if (activeView === `list:${id}`) setActiveView("myday");
    setDeleteListOpen(false);
    setManageMembersOpen(false);
    try {
      await fetch(`${apiBase}/api/DeleteTodoList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id })
      });
    } finally {
      loadData();
    }
  }, [apiBase, activeView, loadData]);

  /* ----------------------------- Derived views ----------------------------- */

  const visibleLists = useMemo(() => {
    if (!memberId) return lists;
    return lists.filter((l) => (l.members || []).includes(memberId) || l.createdBy === memberId || l.createdBy === userEmail);
  }, [lists, memberId, userEmail]);

  const inMyDay = useCallback((t) => {
    if ((t.myDayBy || []).includes(myKey)) return true;
    const due = t.dueDate ? t.dueDate.slice(0, 10) : null;
    return due === todayStr() || due === tomorrowStr();
  }, [myKey]);

  const currentList = activeView.startsWith("list:")
    ? lists.find((l) => l.id === activeView.slice(5))
    : null;

  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (activeView === "myday") {
      result = tasks.filter(inMyDay);
    } else if (activeView === "important") {
      result = tasks.filter((t) => t.important);
    } else if (activeView === "assigned") {
      result = tasks.filter((t) => memberId && (t.assignedTo || []).includes(memberId));
    } else if (activeView === "all") {
      result = tasks;
    } else if (activeView === "tasks") {
      result = tasks.filter((t) => !t.listId);
    } else if (activeView.startsWith("list:")) {
      const listId = activeView.slice(5);
      result = tasks.filter((t) => t.listId === listId);
    }
    return result;
  }, [tasks, activeView, inMyDay, memberId]);

  const activeTasks = filteredTasks.filter((t) => !t.completed);
  const completedTasks = filteredTasks.filter((t) => t.completed);

  const sortedActive = useMemo(() => {
    return [...activeTasks].sort((a, b) => {
      if (a.important !== b.important) return a.important ? -1 : 1;
      const ad = a.dueDate || "9999";
      const bd = b.dueDate || "9999";
      if (ad !== bd) return ad < bd ? -1 : 1;
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });
  }, [activeTasks]);

  const selectedTask = tasks.find((t) => t.id === selectedTaskId) || null;

  const viewTitle = (() => {
    const smart = SMART_VIEWS.find((v) => v.id === activeView);
    if (smart) return smart.label;
    if (activeView === "tasks") return "Tasks";
    if (currentList) return currentList.name;
    return "Tasks";
  })();

  const handleQuickAdd = useCallback(async (e) => {
    e.preventDefault();
    const title = quickAdd.trim();
    if (!title) return;
    setQuickAdd("");
    const payload = { title };
    // Default the new task to the currently selected user.
    if (memberId) payload.assignedTo = [memberId];
    // Default the new task into the current context.
    if (activeView === "important") payload.important = true;
    if (activeView === "myday") payload.myDayBy = myKey ? [myKey] : [];
    if (activeView === "tasks") payload.listId = null;
    if (activeView.startsWith("list:")) payload.listId = activeView.slice(5);
    try {
      await createTask(payload);
    } catch (err) {
      setError(err?.message || "Could not add task");
    }
  }, [quickAdd, activeView, myKey, memberId, createTask]);

  const defaultListSelected = activeView === "tasks";
  const tasksInboxCount = tasks.filter((t) => !t.completed && !t.listId).length;
  const listIncompleteCount = currentList
    ? tasks.filter((t) => t.listId === currentList.id && !t.completed).length
    : 0;

  const openNewListModal = useCallback(() => {
    setNewListOpen(true);
    setSidebarOpen(false);
  }, []);

  const renderSwipeTaskRow = (t) => (
    <li
      key={t.id}
      className={`todo-swipe-row ${swipeOpenTaskId === t.id ? "open" : ""}`}
      onTouchStart={(e) => onRowTouchStart(t.id, e)}
      onTouchMove={(e) => onRowTouchMove(t.id, e)}
      onTouchEnd={onRowTouchEnd}
    >
      <button type="button" className="todo-swipe-delete" onClick={() => removeTask(t.id)}>
        Delete
      </button>
      <TaskRow
        task={t}
        memberById={memberById}
        selected={t.id === selectedTaskId}
        showListTag={!activeView.startsWith("list:") && activeView !== "tasks"}
        listName={t.listId ? lists.find((l) => l.id === t.listId)?.name : null}
        showAssignees={activeView.startsWith("list:")}
        onToggleDone={() => patchTask(t.id, { completed: !t.completed })}
        onToggleImportant={() => patchTask(t.id, { important: !t.important })}
        onOpen={() => {
          setSwipeOpenTaskId(null);
          setSelectedTaskId(t.id);
        }}
      />
    </li>
  );

  return (
    <div className="todo-page">
      <div className="todo-page-top">
        <div id={APPROVAL_BELL_SLOTS.todoMobile} className="approval-bell-slot approval-bell-slot--inline" aria-hidden="true" />
        <button
          type="button"
          className="todo-sidebar-toggle"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Toggle lists"
        >
          {"\u2630"} Lists
        </button>
      </div>

      <aside className={`todo-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="todo-sidebar-header">
          <span className="todo-app-title">To-Do</span>
          <MemberPill
            familyMembers={familyMembers}
            memberId={memberId}
            memberById={memberById}
            onChangeMemberId={onChangeMemberId}
          />
        </div>

        <nav className="todo-smart-list">
          {SMART_VIEWS.map((v) => {
            let count = 0;
            if (v.id === "myday") count = tasks.filter((t) => !t.completed && inMyDay(t)).length;
            else if (v.id === "important") count = tasks.filter((t) => !t.completed && t.important).length;
            else if (v.id === "assigned") count = tasks.filter((t) => !t.completed && memberId && (t.assignedTo || []).includes(memberId)).length;
            else count = tasks.filter((t) => !t.completed).length;
            return (
              <button
                key={v.id}
                type="button"
                className={`todo-nav-item ${activeView === v.id ? "active" : ""}`}
                onClick={() => { setActiveView(v.id); setSidebarOpen(false); }}
              >
                <span className="todo-nav-icon" aria-hidden="true">{v.icon}</span>
                <span className="todo-nav-label">{v.label}</span>
                {count > 0 && <span className="todo-nav-count">{count}</span>}
              </button>
            );
          })}
          <button
            type="button"
            className={`todo-nav-item ${defaultListSelected ? "active" : ""}`}
            onClick={() => { setActiveView("tasks"); setSidebarOpen(false); }}
          >
            <span className="todo-nav-icon" aria-hidden="true">{"\u2261"}</span>
            <span className="todo-nav-label">Tasks</span>
            {tasksInboxCount > 0 && <span className="todo-nav-count">{tasksInboxCount}</span>}
          </button>
        </nav>

        <div className="todo-sidebar-divider">Lists</div>

        <nav className="todo-list-nav">
          {visibleLists.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`todo-nav-item ${activeView === `list:${l.id}` ? "active" : ""}`}
              onClick={() => { setActiveView(`list:${l.id}`); setSidebarOpen(false); }}
            >
              <span className="todo-nav-icon" aria-hidden="true" style={l.color ? { color: l.color } : undefined}>{"\u2630"}</span>
              <span className="todo-nav-label">{l.name}</span>
              <span className="todo-nav-count">{tasks.filter((t) => !t.completed && t.listId === l.id).length || ""}</span>
            </button>
          ))}
        </nav>

        <button type="button" className="todo-new-list-btn" onClick={openNewListModal}>
          + New list
        </button>
      </aside>

      {sidebarOpen && <div className="todo-scrim" onClick={() => setSidebarOpen(false)} />}

      <main className="todo-main">
        <header className="todo-main-header">
          <div id={APPROVAL_BELL_SLOTS.todoDesktop} className="approval-bell-slot" aria-hidden="true" />
          <div className="todo-main-title-row">
            <div className="todo-main-title-group">
              <h1 className="todo-main-title">{viewTitle}</h1>
              {activeView === "myday" && (
                <span className="todo-main-date">{format(new Date(), "EEEE, MMMM d")}</span>
              )}
            </div>
            {currentList && (
              <ListHeaderMenu
                onManageMembers={() => setManageMembersOpen(true)}
                onDeleteList={() => setDeleteListOpen(true)}
              />
            )}
          </div>
          <MemberPill
            className="todo-member-pill-mobile"
            familyMembers={familyMembers}
            memberId={memberId}
            memberById={memberById}
            onChangeMemberId={onChangeMemberId}
          />
        </header>

        {error && <div className="todo-error">{error}</div>}

        <div className="todo-task-scroll">
          {loading ? (
            <div className="todo-empty">Loading...</div>
          ) : sortedActive.length === 0 && completedTasks.length === 0 ? (
            <div className="todo-empty">
              {activeView === "myday"
                ? "Nothing for today yet. Tasks due today or tomorrow show up here automatically."
                : "No tasks here yet."}
            </div>
          ) : (
            <ul className="todo-task-list">
              {sortedActive.map(renderSwipeTaskRow)}
            </ul>
          )}

          {completedTasks.length > 0 && (
            <div className="todo-completed-section">
              <button type="button" className="todo-completed-toggle" onClick={() => setShowCompleted((v) => !v)}>
                {showCompleted ? "\u25BC" : "\u25B6"} Completed ({completedTasks.length})
              </button>
              {showCompleted && (
                <ul className="todo-task-list">
                  {completedTasks.map(renderSwipeTaskRow)}
                </ul>
              )}
            </div>
          )}
        </div>

        <form className="todo-quick-add" onSubmit={handleQuickAdd}>
          <span className="todo-quick-add-icon" aria-hidden="true">+</span>
          <input
            type="text"
            placeholder={`Add a task${
              currentList ? ` to ${currentList.name}`
                : activeView === "myday" ? " to My Day"
                : activeView === "tasks" ? " to Tasks"
                : ""
            }`}
            value={quickAdd}
            onChange={(e) => setQuickAdd(e.target.value)}
          />
          {quickAdd.trim() && <button type="submit">Add</button>}
        </form>
      </main>

      {selectedTask && (
        <TaskDetail
          key={selectedTask.id}
          task={selectedTask}
          apiBase={apiBase}
          familyMembers={familyMembers}
          myKey={myKey}
          inMyDay={(selectedTask.myDayBy || []).includes(myKey)}
          onClose={() => setSelectedTaskId(null)}
          onPatch={(patch) => patchTask(selectedTask.id, patch)}
          onDelete={() => removeTask(selectedTask.id)}
        />
      )}

      {identityOpen && (
        <IdentityPicker
          familyMembers={familyMembers}
          userEmail={userEmail}
          currentId={memberId}
          onPick={(id) => { onChangeMemberId?.(id); setIdentityOpen(false); }}
          onClose={() => setIdentityOpen(false)}
        />
      )}

      {newListOpen && (
        <NewListModal
          familyMembers={familyMembers}
          creating={creatingList}
          onCreate={createList}
          onClose={() => setNewListOpen(false)}
        />
      )}

      {currentList && manageMembersOpen && (
        <ManageMembersModal
          list={currentList}
          familyMembers={familyMembers}
          onSave={(members) => {
            patchList(currentList.id, { members });
            setManageMembersOpen(false);
          }}
          onClose={() => setManageMembersOpen(false)}
        />
      )}

      {currentList && deleteListOpen && (
        <DeleteListModal
          listName={currentList.name}
          incompleteCount={listIncompleteCount}
          onConfirm={() => removeList(currentList.id)}
          onClose={() => setDeleteListOpen(false)}
        />
      )}
    </div>
  );
}

/* ----------------------------- Task row ----------------------------- */

function TaskRow({ task, memberById, selected, showListTag, listName, showAssignees, onToggleDone, onToggleImportant, onOpen }) {
  const due = task.dueDate ? formatDueLabel(task.dueDate) : null;
  const overdue = isOverdue(task.dueDate, task.completed);
  const assignees = (task.assignedTo || []).map((id) => memberById[id]).filter(Boolean);
  return (
    <div className={`todo-row ${selected ? "selected" : ""} ${task.completed ? "done" : ""}`}>
      <button
        type="button"
        className={`todo-check ${task.completed ? "checked" : ""}`}
        onClick={onToggleDone}
        aria-label={task.completed ? "Mark not done" : "Mark done"}
      >
        {task.completed ? "\u2713" : ""}
      </button>
      <button type="button" className="todo-row-body" onClick={onOpen}>
        <span className="todo-row-title">{task.title}</span>
        <span className="todo-row-meta">
          {showListTag && listName && <span className="todo-tag">{listName}</span>}
          {task.repeat?.frequency && task.repeat.frequency !== "none" && (
            <span className="todo-meta-bit" title="Repeats">{"\u21BB"}</span>
          )}
          {(task.attachments || []).length > 0 && (
            <span className="todo-meta-bit" title="Has attachment">{"\u{1F4CE}"}</span>
          )}
          {task.notes && <span className="todo-meta-bit" title="Has notes">{"\u270E"}</span>}
          {due && <span className={`todo-meta-bit ${overdue ? "overdue" : ""}`}>{"\u{1F4C5}"} {due}</span>}
          {(showAssignees || assignees.length > 0) && (
            <span className="todo-avatars">
              {assignees.length > 0 ? (
                assignees.slice(0, 3).map((m) => (
                  <span key={m.id} className="todo-avatar" style={{ background: m.color || "#888" }} title={m.name}>
                    {initialsFor(m.name)}
                  </span>
                ))
              ) : (
                <span className="todo-meta-bit todo-unassigned">Unassigned</span>
              )}
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        className={`todo-star ${task.important ? "on" : ""}`}
        onClick={onToggleImportant}
        aria-label={task.important ? "Remove importance" : "Mark important"}
      >
        {task.important ? "\u2605" : "\u2606"}
      </button>
    </div>
  );
}

/* ----------------------------- Detail panel ----------------------------- */

function TaskDetail({ task, apiBase, familyMembers, myKey, inMyDay, onClose, onPatch, onDelete }) {
  const [title, setTitle] = useState(task.title);
  const [details, setDetails] = useState(task.details || "");
  const [notes, setNotes] = useState(task.notes || "");
  const [uploading, setUploading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'remind' | 'due' | 'repeat' | 'assign'
  const [menuRect, setMenuRect] = useState(null);
  const [picker, setPicker] = useState(null); // 'remind' | 'due'
  const fileRef = useRef(null);

  useEffect(() => {
    setTitle(task.title);
    setDetails(task.details || "");
    setNotes(task.notes || "");
    setOpenMenu(null);
    setPicker(null);
  }, [task.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openMenuAt = (name, e) => {
    if (openMenu === name) { setOpenMenu(null); return; }
    const row = e.currentTarget.closest(".todo-row-action") || e.currentTarget;
    setMenuRect(row.getBoundingClientRect());
    setOpenMenu(name);
  };

  const repeat = task.repeat || { frequency: "none" };
  const remind = task.remindMe || { enabled: false, at: null, channel: null };
  const assignees = (task.assignedTo || []).map((id) => familyMembers.find((m) => m.id === id)).filter(Boolean);

  const closeAll = () => { setOpenMenu(null); setPicker(null); };

  const toggleAssignee = (id) => {
    const cur = task.assignedTo || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onPatch({ assignedTo: next });
  };

  const toggleMyDay = () => {
    const cur = task.myDayBy || [];
    if (!myKey) return;
    const next = cur.includes(myKey) ? cur.filter((x) => x !== myKey) : [...cur, myKey];
    onPatch({ myDayBy: next });
  };

  const setReminder = (value) => {
    onPatch({ remindMe: { ...remind, enabled: !!value, at: value || null, channel: remind.channel || "email", sent: false } });
    closeAll();
  };
  const clearReminder = () => onPatch({ remindMe: { enabled: false, at: null, channel: remind.channel || null, sent: false } });

  const setDue = (value) => { onPatch({ dueDate: value || null }); closeAll(); };
  const clearDue = () => onPatch({ dueDate: null });

  const setRepeatFreq = (frequency) => {
    onPatch({ repeat: { ...repeat, frequency } });
    if (frequency !== "custom") closeAll();
  };
  const clearRepeat = () => onPatch({ repeat: { frequency: "none" } });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const res = await fetch(`${apiBase}/api/UploadTodoAttachment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: file.name, contentType: file.type, data: dataUrl })
      });
      if (!res.ok) throw new Error(`Upload error ${res.status}`);
      const data = await res.json();
      onPatch({ attachments: [...(task.attachments || []), data.attachment] });
    } catch {
      // swallow; could surface error
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    onPatch({ attachments: (task.attachments || []).filter((a) => a.id !== id) });
  };

  const repeatLabel = REPEAT_OPTIONS.find((o) => o.value === repeat.frequency)?.label || "Repeat";
  const remindDate = parseLocal(remind.at);
  const dueDate = parseLocal(task.dueDate);

  return (
    <>
      <div className="todo-detail-scrim" onClick={onClose} />
      <aside className="todo-detail" role="dialog" aria-label="Task details">
        <div className="todo-detail-head">
          <button
            type="button"
            className={`todo-check big ${task.completed ? "checked" : ""}`}
            onClick={() => onPatch({ completed: !task.completed })}
          >
            {task.completed ? "\u2713" : ""}
          </button>
          <input
            className="todo-detail-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== task.title && onPatch({ title: title.trim() })}
          />
          <button
            type="button"
            className={`todo-star ${task.important ? "on" : ""}`}
            onClick={() => onPatch({ important: !task.important })}
            aria-label="Important"
          >
            {task.important ? "\u2605" : "\u2606"}
          </button>
          <button type="button" className="todo-detail-close" onClick={onClose} aria-label="Close">{"\u2715"}</button>
        </div>

        <div className="todo-detail-body">
          <button type="button" className={`todo-row-action ${inMyDay ? "active" : ""}`} onClick={toggleMyDay}>
            <span className="todo-row-action-icon" aria-hidden="true">{"\u2600"}</span>
            <span className="todo-row-action-label">{inMyDay ? "Added to My Day" : "Add to My Day"}</span>
            {inMyDay && <span className="todo-row-x" role="button" tabIndex={0} aria-label="Remove from My Day" onClick={(e) => { e.stopPropagation(); toggleMyDay(); }}>{"\u2715"}</span>}
          </button>

          {/* Scheduling group: reminder / due / repeat */}
          <div className="todo-card">
            {/* Reminder */}
            <button type="button" className={`todo-row-action ${remind.at && remindDate ? "set" : ""}`} onClick={(e) => openMenuAt("remind", e)}>
              <span className="todo-row-action-icon" aria-hidden="true">{"\u23F0"}</span>
              {remind.at && remindDate ? (
                <span className="todo-row-action-text">
                  <span className="todo-row-action-label">Remind me at {timeLabel(remindDate)}</span>
                  <span className="todo-row-action-sub">
                    {format(remindDate, "EEE, MMM d")}
                    {" \u00B7 "}
                    <select
                      className="todo-inline-select"
                      value={remind.channel || "email"}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => onPatch({ remindMe: { ...remind, channel: e.target.value } })}
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                    </select>
                  </span>
                </span>
              ) : (
                <span className="todo-row-action-label">Remind me</span>
              )}
              {remind.at && remindDate && (
                <span className="todo-row-x" role="button" tabIndex={0} aria-label="Clear reminder" onClick={(e) => { e.stopPropagation(); clearReminder(); }}>{"\u2715"}</span>
              )}
            </button>

            {/* Due date */}
            <button type="button" className={`todo-row-action ${task.dueDate && dueDate ? "set" : ""}`} onClick={(e) => openMenuAt("due", e)}>
              <span className="todo-row-action-icon" aria-hidden="true">{"\u{1F4C5}"}</span>
              <span className="todo-row-action-label">{task.dueDate && dueDate ? `Due ${formatDueLabel(task.dueDate)}` : "Add due date"}</span>
              {task.dueDate && dueDate && (
                <span className="todo-row-x" role="button" tabIndex={0} aria-label="Clear due date" onClick={(e) => { e.stopPropagation(); clearDue(); }}>{"\u2715"}</span>
              )}
            </button>

            {/* Repeat */}
            <button type="button" className={`todo-row-action ${repeat.frequency && repeat.frequency !== "none" ? "set" : ""}`} onClick={(e) => openMenuAt("repeat", e)}>
              <span className="todo-row-action-icon" aria-hidden="true">{"\u21BB"}</span>
              <span className="todo-row-action-label">
                {repeat.frequency && repeat.frequency !== "none"
                  ? `${repeatLabel}${repeat.frequency === "custom" && repeat.interval ? ` (every ${repeat.interval} ${repeat.unit || "days"})` : ""}`
                  : "Repeat"}
              </span>
              {repeat.frequency && repeat.frequency !== "none" && (
                <span className="todo-row-x" role="button" tabIndex={0} aria-label="Clear repeat" onClick={(e) => { e.stopPropagation(); clearRepeat(); }}>{"\u2715"}</span>
              )}
            </button>
            {repeat.frequency === "custom" && (
              <div className="todo-custom-repeat">
                <span>Every</span>
                <input
                  type="number"
                  min="1"
                  value={repeat.interval || 1}
                  onChange={(e) => onPatch({ repeat: { ...repeat, interval: Math.max(1, parseInt(e.target.value, 10) || 1) } })}
                />
                <select
                  value={repeat.unit || "days"}
                  onChange={(e) => onPatch({ repeat: { ...repeat, unit: e.target.value } })}
                >
                  <option value="days">days</option>
                  <option value="weeks">weeks</option>
                  <option value="months">months</option>
                  <option value="years">years</option>
                </select>
              </div>
            )}
          </div>

          {/* Assign to */}
          <div className="todo-card">
            <button type="button" className={`todo-row-action ${assignees.length ? "set" : ""}`} onClick={(e) => openMenuAt("assign", e)}>
              <span className="todo-row-action-icon" aria-hidden="true">{"\u{1F464}"}</span>
              {assignees.length === 0 ? (
                <span className="todo-row-action-label">Assign to</span>
              ) : (
                <span className="todo-row-action-text">
                  <span className="todo-row-action-label">{assignees.map((m) => m.name).join(", ")}</span>
                </span>
              )}
              <span className="todo-avatars">
                {assignees.slice(0, 4).map((m) => (
                  <span key={m.id} className="todo-avatar sm-stack" style={{ background: m.color || "#888" }} title={m.name}>{initialsFor(m.name)}</span>
                ))}
              </span>
            </button>
          </div>

          {openMenu === "remind" && (
            <WhenMenu
              variant="remind"
              rect={menuRect}
              onPick={setReminder}
              onPickCustom={() => { setOpenMenu(null); setPicker("remind"); }}
              onClose={() => setOpenMenu(null)}
            />
          )}
          {openMenu === "due" && (
            <WhenMenu
              variant="due"
              rect={menuRect}
              onPick={setDue}
              onPickCustom={() => { setOpenMenu(null); setPicker("due"); }}
              onClose={() => setOpenMenu(null)}
            />
          )}
          {openMenu === "repeat" && (
            <Popover rect={menuRect} onClose={() => setOpenMenu(null)}>
              {REPEAT_OPTIONS.filter((o) => o.value !== "none").map((o) => (
                <button key={o.value} type="button" className={`todo-menu-item ${repeat.frequency === o.value ? "on" : ""}`} onClick={() => setRepeatFreq(o.value)}>
                  <span className="todo-menu-icon" aria-hidden="true">{"\u21BB"}</span>
                  <span className="todo-menu-item-label">{o.label}</span>
                  {repeat.frequency === o.value && <span className="todo-menu-check" aria-hidden="true">{"\u2713"}</span>}
                </button>
              ))}
            </Popover>
          )}
          {openMenu === "assign" && (
            <Popover rect={menuRect} width={240} onClose={() => setOpenMenu(null)}>
              {familyMembers.length === 0 && <div className="todo-menu-empty">No family members loaded.</div>}
              {familyMembers.map((m) => {
                const on = (task.assignedTo || []).includes(m.id);
                return (
                  <button key={m.id} type="button" className={`todo-menu-item ${on ? "on" : ""}`} onClick={() => toggleAssignee(m.id)}>
                    <span className="todo-avatar sm" style={{ background: m.color || "#888" }}>{initialsFor(m.name)}</span>
                    <span className="todo-menu-item-label">{m.name}</span>
                    {on && <span className="todo-menu-check" aria-hidden="true">{"\u2713"}</span>}
                  </button>
                );
              })}
            </Popover>
          )}

          {/* Details */}
          <div className="todo-card">
            <div className="todo-inline-edit">
              <span className="todo-row-action-icon" aria-hidden="true">{"\u270E"}</span>
              <textarea
                rows={1}
                placeholder="Add details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                onBlur={() => details !== (task.details || "") && onPatch({ details })}
              />
            </div>
          </div>

          {/* Files */}
          <div className="todo-card">
            {(task.attachments || []).map((a) => (
              <div key={a.id} className="todo-row-action set">
                <span className="todo-row-action-icon" aria-hidden="true">{"\u{1F4CE}"}</span>
                <a className="todo-row-action-label todo-file-link" href={a.url} target="_blank" rel="noreferrer">{a.name}</a>
                <button type="button" className="todo-row-x" onClick={() => removeAttachment(a.id)} aria-label="Remove file">{"\u2715"}</button>
              </div>
            ))}
            <button type="button" className="todo-row-action" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <span className="todo-row-action-icon" aria-hidden="true">{"\u{1F4CE}"}</span>
              <span className="todo-row-action-label">{uploading ? "Uploading..." : "Add file"}</span>
            </button>
            <input ref={fileRef} type="file" onChange={onUpload} disabled={uploading} hidden />
          </div>

          {/* Notes */}
          <div className="todo-card notes">
            <textarea
              rows={3}
              placeholder="Add note"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => notes !== (task.notes || "") && onPatch({ notes })}
            />
          </div>
        </div>

        {picker && (
          <DateTimePopover
            initial={picker === "remind" ? remind.at : task.dueDate}
            withTime={picker === "remind"}
            onCancel={() => setPicker(null)}
            onSave={(value) => { if (picker === "remind") setReminder(value); else setDue(value); }}
          />
        )}

        <div className="todo-detail-foot">
          <span className="todo-created">
            {task.createdAt ? `Created ${format(parseISO(task.createdAt), "MMM d, yyyy")}` : ""}
          </span>
          <button type="button" className="todo-delete-task" onClick={onDelete}>
            <span aria-hidden="true">{"\u{1F5D1}"}</span> Delete task
          </button>
        </div>
      </aside>
    </>
  );
}

/* ----------------------------- Popover (fixed, escapes clipping) ----------------------------- */

function menuPosition(rect, width) {
  const w = width || (rect ? Math.max(rect.width, 220) : 240);
  if (!rect) return { position: "fixed", left: 16, top: 90, width: w, zIndex: 1100 };
  let left = rect.left;
  if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
  if (left < 8) left = 8;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUp = spaceBelow < 300 && rect.top > spaceBelow;
  const style = { position: "fixed", left, width: w, zIndex: 1100 };
  if (openUp) style.bottom = Math.max(8, window.innerHeight - rect.top + 4);
  else style.top = rect.bottom + 4;
  return style;
}

function Popover({ rect, width, onClose, children }) {
  return (
    <>
      <div className="todo-menu-backdrop" onClick={onClose} />
      <div className="todo-menu-list" style={menuPosition(rect, width)}>{children}</div>
    </>
  );
}

/* ----------------------------- When (quick) menu ----------------------------- */

function WhenMenu({ variant, rect, onPick, onPickCustom, onClose }) {
  const now = new Date();
  let options;
  if (variant === "remind") {
    const later = setMinutes(setHours(now, now.getHours() + 3), 0);
    const tom = setMinutes(setHours(addDays(now, 1), 9), 0);
    const next = setMinutes(setHours(addDays(now, 7), 9), 0);
    options = [
      { id: "later", icon: "\u{1F551}", label: "Later today", detail: timeLabel(later), value: toLocalDateTime(later) },
      { id: "tom", icon: "\u2192", label: "Tomorrow", detail: format(tom, "EEE, h:mm a"), value: toLocalDateTime(tom) },
      { id: "next", icon: "\u00BB", label: "Next week", detail: format(next, "EEE, h:mm a"), value: toLocalDateTime(next) }
    ];
  } else {
    const tom = addDays(now, 1);
    const next = addDays(now, 7);
    options = [
      { id: "today", icon: "\u{1F551}", label: "Today", detail: format(now, "EEE"), value: toLocalDate(now) },
      { id: "tom", icon: "\u2192", label: "Tomorrow", detail: format(tom, "EEE"), value: toLocalDate(tom) },
      { id: "next", icon: "\u00BB", label: "Next week", detail: format(next, "EEE"), value: toLocalDate(next) }
    ];
  }
  return (
    <Popover rect={rect} onClose={onClose}>
      {options.map((o) => (
        <button key={o.id} type="button" className="todo-menu-item" onClick={() => onPick(o.value)}>
          <span className="todo-menu-icon" aria-hidden="true">{o.icon}</span>
          <span className="todo-menu-item-label">{o.label}</span>
          <span className="todo-menu-detail">{o.detail}</span>
        </button>
      ))}
      <button type="button" className="todo-menu-item with-divider" onClick={onPickCustom}>
        <span className="todo-menu-icon" aria-hidden="true">{"\u{1F4C5}"}</span>
        <span className="todo-menu-item-label">{variant === "remind" ? "Pick a date & time" : "Pick a date"}</span>
      </button>
    </Popover>
  );
}

/* ----------------------------- Date & time popover ----------------------------- */

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function DateTimePopover({ initial, withTime, onSave, onCancel }) {
  const init = parseLocal(initial) || setMinutes(setHours(new Date(), 9), 0);
  const [viewMonth, setViewMonth] = useState(startOfMonth(init));
  const [selected, setSelected] = useState(init);
  const [hour12, setHour12] = useState(((init.getHours() + 11) % 12) + 1);
  const [minute, setMinute] = useState(init.getMinutes());
  const [ampm, setAmpm] = useState(init.getHours() >= 12 ? "PM" : "AM");

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewMonth)),
    end: endOfWeek(endOfMonth(viewMonth))
  });

  const minuteOptions = Array.from(new Set([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, minute])).sort((a, b) => a - b);

  const handleSave = () => {
    let d = new Date(selected);
    if (withTime) {
      let h = hour12 % 12;
      if (ampm === "PM") h += 12;
      d = setMinutes(setHours(d, h), minute);
      onSave(toLocalDateTime(d));
    } else {
      onSave(toLocalDate(d));
    }
  };

  return (
    <div className="todo-picker-wrap">
      <div className="todo-menu-backdrop" onClick={onCancel} />
      <div className="todo-picker" role="dialog" aria-label="Pick date and time">
        <div className="todo-picker-head">
          <span className="todo-picker-month">{format(viewMonth, "MMMM yyyy")}</span>
          <div className="todo-picker-nav">
            <button type="button" onClick={() => setViewMonth(addMonths(viewMonth, -1))} aria-label="Previous month">{"\u2039"}</button>
            <button type="button" onClick={() => setViewMonth(addMonths(viewMonth, 1))} aria-label="Next month">{"\u203A"}</button>
          </div>
        </div>
        <div className="todo-picker-weekdays">
          {WEEKDAY_LABELS.map((w) => <span key={w}>{w}</span>)}
        </div>
        <div className="todo-picker-grid">
          {days.map((d) => {
            const muted = !isSameMonth(d, viewMonth);
            const sel = isSameDay(d, selected);
            const today = isSameDay(d, new Date());
            return (
              <button
                key={d.toISOString()}
                type="button"
                className={`todo-picker-day ${muted ? "muted" : ""} ${sel ? "sel" : ""} ${today ? "today" : ""}`}
                onClick={() => setSelected(d)}
              >
                {format(d, "d")}
              </button>
            );
          })}
        </div>
        {withTime && (
          <div className="todo-picker-time">
            <select value={hour12} onChange={(e) => setHour12(parseInt(e.target.value, 10))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span>:</span>
            <select value={minute} onChange={(e) => setMinute(parseInt(e.target.value, 10))}>
              {minuteOptions.map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
            </select>
            <select value={ampm} onChange={(e) => setAmpm(e.target.value)}>
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        )}
        <div className="todo-picker-foot">
          <button type="button" className="todo-picker-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="todo-picker-save" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- List header menu ----------------------------- */

function ListHeaderMenu({ onManageMembers, onDeleteList }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="todo-list-menu-wrap">
      <button
        type="button"
        className="todo-list-menu-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="List options"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {"\u22EF"}
      </button>
      {open && (
        <>
          <div className="todo-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="todo-list-menu" role="menu">
            <button
              type="button"
              role="menuitem"
              className="todo-menu-item"
              onClick={() => { setOpen(false); onManageMembers(); }}
            >
              Manage members
            </button>
            <button
              type="button"
              role="menuitem"
              className="todo-menu-item danger"
              onClick={() => { setOpen(false); onDeleteList(); }}
            >
              Delete list
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MemberCheckboxList({ familyMembers, selectedIds, onChange }) {
  const toggle = (id) => {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange([...set]);
  };
  const toggleAll = () => {
    if (selectedIds.length === familyMembers.length) onChange([]);
    else onChange(familyMembers.map((m) => m.id));
  };
  const allSelected = familyMembers.length > 0 && selectedIds.length === familyMembers.length;
  return (
    <div className="todo-member-pick">
      <label className="todo-share-row todo-share-row-all">
        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
        <span className="todo-member-pick-label">Everyone</span>
      </label>
      {familyMembers.map((m) => (
        <label key={m.id} className="todo-share-row">
          <input
            type="checkbox"
            checked={selectedIds.includes(m.id)}
            onChange={() => toggle(m.id)}
          />
          <span className="todo-avatar sm" style={{ background: m.color || "#888" }}>{initialsFor(m.name)}</span>
          <span className="todo-member-pick-label">{m.name}</span>
        </label>
      ))}
    </div>
  );
}

function NewListModal({ familyMembers, creating, onCreate, onClose }) {
  const [name, setName] = useState("");
  const [members, setMembers] = useState(() => familyMembers.map((m) => m.id));
  const canCreate = name.trim().length > 0 && !creating;
  return (
    <div className="todo-detail-scrim todo-overlay-center" onClick={onClose}>
      <div className="todo-list-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="new-list-title">
        <h2 id="new-list-title">New list</h2>
        <p className="todo-list-modal-sub">Choose a name and who can see this list.</p>
        <label className="todo-list-modal-field">
          <span>List name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Groceries, Vacation"
            autoFocus
          />
        </label>
        <div className="todo-list-modal-section">
          <span className="todo-share-title">Share with</span>
          <MemberCheckboxList familyMembers={familyMembers} selectedIds={members} onChange={setMembers} />
        </div>
        <div className="todo-list-modal-actions">
          <button type="button" className="todo-modal-btn secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="todo-modal-btn primary"
            disabled={!canCreate}
            onClick={() => onCreate({ name: name.trim(), members })}
          >
            {creating ? "Creating…" : "Create list"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageMembersModal({ list, familyMembers, onSave, onClose }) {
  const [members, setMembers] = useState(() => list.members || []);
  return (
    <div className="todo-detail-scrim todo-overlay-center" onClick={onClose}>
      <div className="todo-list-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="manage-members-title">
        <h2 id="manage-members-title">Manage members</h2>
        <p className="todo-list-modal-sub">Choose who can see <strong>{list.name}</strong>.</p>
        <div className="todo-list-modal-section">
          <MemberCheckboxList familyMembers={familyMembers} selectedIds={members} onChange={setMembers} />
        </div>
        <div className="todo-list-modal-actions">
          <button type="button" className="todo-modal-btn secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="todo-modal-btn primary" onClick={() => onSave(members)}>Save</button>
        </div>
      </div>
    </div>
  );
}

function DeleteListModal({ listName, incompleteCount, onConfirm, onClose }) {
  const taskWord = incompleteCount === 1 ? "task" : "tasks";
  return (
    <div className="todo-detail-scrim todo-overlay-center" onClick={onClose}>
      <div className="todo-list-modal todo-list-modal-danger" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="delete-list-title">
        <h2 id="delete-list-title">Delete list?</h2>
        <p className="todo-list-modal-sub">
          {incompleteCount > 0 ? (
            <>
              <strong>{listName}</strong> has {incompleteCount} incomplete {taskWord}.
              Are you sure you want to delete this list? Its tasks will move back to Tasks.
            </>
          ) : (
            <>
              Are you sure you want to delete <strong>{listName}</strong>? Its tasks will move back to Tasks.
            </>
          )}
        </p>
        <div className="todo-list-modal-actions">
          <button type="button" className="todo-modal-btn secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="todo-modal-btn danger" onClick={onConfirm}>Delete list</button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Member pill (identity dropdown) ----------------------------- */

function MemberPill({ familyMembers, memberId, memberById, onChangeMemberId, className }) {
  const [open, setOpen] = useState(false);
  const me = memberId ? memberById[memberId] : null;
  const color = me?.color || null;
  return (
    <div className={`todo-member-pill-wrap ${className || ""}`}>
      <button
        type="button"
        className={`todo-member-pill ${me ? "has-member" : ""}`}
        style={color ? { background: color, borderColor: color } : undefined}
        onClick={() => setOpen((v) => !v)}
        title="Switch family member"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {me && (
          <span className="todo-member-pill-avatar">{initialsFor(me.name)}</span>
        )}
        <span className="todo-member-pill-name">{me ? me.name : "Set who you are"}</span>
        <span className="todo-member-pill-caret" aria-hidden="true">{"\u25BE"}</span>
      </button>
      {open && (
        <>
          <div className="todo-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="todo-member-menu" role="listbox">
            {familyMembers.length === 0 && <div className="todo-menu-empty">No family members loaded.</div>}
            {familyMembers.map((m) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={m.id === memberId}
                className={`todo-member-option ${m.id === memberId ? "on" : ""}`}
                onClick={() => { onChangeMemberId?.(m.id); setOpen(false); }}
              >
                <span className="todo-avatar sm" style={{ background: m.color || "#888" }}>{initialsFor(m.name)}</span>
                <span className="todo-member-option-name">{m.name}</span>
                {m.id === memberId && <span className="todo-menu-check" aria-hidden="true">{"\u2713"}</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------------- Identity picker ----------------------------- */

function IdentityPicker({ familyMembers, userEmail, currentId, onPick, onClose }) {
  return (
    <div className="todo-detail-scrim todo-overlay-center" onClick={onClose}>
      <div className="todo-identity-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Who are you?</h2>
        <p>Pick your name so "My Day" and "Assigned to me" work for you{userEmail ? ` (${userEmail})` : ""}.</p>
        <div className="todo-identity-grid">
          {familyMembers.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`todo-identity-option ${currentId === m.id ? "on" : ""}`}
              onClick={() => onPick(m.id)}
            >
              <span className="todo-avatar" style={{ background: m.color || "#888" }}>{initialsFor(m.name)}</span>
              <span>{m.name}</span>
              {m.email && <span className="todo-identity-email">{m.email}</span>}
            </button>
          ))}
        </div>
        <button type="button" className="todo-identity-skip" onClick={onClose}>Not now</button>
      </div>
    </div>
  );
}
